import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";

const FOCUSABLE =
  'button:not([disabled]), [href], select, input, [tabindex]:not([tabindex="-1"])';

export default function FilterSheet({
  open,
  onClose,
  onClearAll,
  resultCount,
  resultNoun = "results",
  children,
}) {
  const sheetRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const titleId = useId();
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    document.body.classList.add("noscroll");
    const sheet = sheetRef.current;
    const focusables = () => (sheet ? [...sheet.querySelectorAll(FOCUSABLE)] : []);

    // Focus the first focusable element inside the sheet body, or fall back to the first in the sheet
    function focusFirstInSheet() {
      const sheetBody = sheet ? sheet.querySelector(".nfl-sheet-body") : null;
      let firstToFocus = null;

      if (sheetBody) {
        const bodyFocusables = [...sheetBody.querySelectorAll(FOCUSABLE)];
        firstToFocus = bodyFocusables[0];
      }

      if (!firstToFocus) {
        const allFocusables = focusables();
        firstToFocus = allFocusables[0];
      }

      if (firstToFocus) {
        firstToFocus.focus();
      }
    }

    focusFirstInSheet();

    function onKeyDown(event) {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab") {
        return;
      }
      const items = focusables();
      if (items.length === 0) {
        return;
      }
      if (sheet && !sheet.contains(document.activeElement)) {
        event.preventDefault();
        focusFirstInSheet();
        return;
      }
      const firstItem = items[0];
      const lastItem = items[items.length - 1];
      if (event.shiftKey && document.activeElement === firstItem) {
        event.preventDefault();
        lastItem.focus();
      } else if (!event.shiftKey && document.activeElement === lastItem) {
        event.preventDefault();
        firstItem.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.classList.remove("noscroll");
    };
  }, [open]);

  if (!open) {
    return null;
  }

  const showLabel =
    typeof resultCount === "number" ? `Show ${resultCount} ${resultNoun}` : "Show results";

  return createPortal(
    <div className="nfl-sheet-root">
      <button
        type="button"
        className="nfl-sheet-scrim"
        tabIndex={-1}
        aria-label="Close filters"
        onClick={() => onCloseRef.current()}
      />
      <div
        className="nfl-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={sheetRef}
      >
        <div className="nfl-sheet-handle" aria-hidden="true" />
        <div className="nfl-sheet-head">
          <h2 id={titleId} className="nfl-sheet-title nfl-display">
            Filters
          </h2>
          {onClearAll ? (
            <button
              type="button"
              className="nfl-link-button"
              onClick={() => {
                onClearAll();
                requestAnimationFrame(() => {
                  const sheet = sheetRef.current;
                  const sheetBody = sheet ? sheet.querySelector(".nfl-sheet-body") : null;
                  const target = sheetBody
                    ? sheetBody.querySelector(FOCUSABLE)
                    : sheet
                    ? sheet.querySelector(FOCUSABLE)
                    : null;
                  if (target) {
                    target.focus();
                  }
                });
              }}
            >
              Clear all
            </button>
          ) : null}
        </div>
        <div className="nfl-sheet-body">{children}</div>
        <div className="nfl-sheet-foot">
          <button
            type="button"
            className="nfl-btn nfl-btn-primary nfl-btn-block"
            onClick={() => onCloseRef.current()}
          >
            {showLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
